/**
 * Parse HTML content pasted from Microsoft Office Word
 * Extracts images and text content with proper image placeholders
 * @param {string} html - HTML string from clipboard
 * @returns {Promise<{images: Array, text: string}>} Parsed content with images and text
 */
export async function parseWordHTML(html) {
  const result = {
    images: [],
    text: '',
  };

  if (!html) {
    return result;
  }

  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Process the body content, replacing images with placeholders
  let imageIndex = 0;
  const imageMap = new Map(); // Map to store image elements and their IDs

  // First pass: identify and mark all images
  const images = doc.querySelectorAll('img');
  images.forEach((img) => {
    const imageId = `[Image@${imageIndex + 1}]`;
    imageMap.set(img, imageId);

    // Replace image with placeholder text node
    const placeholder = doc.createTextNode(imageId);
    img.parentNode.replaceChild(placeholder, img);

    imageIndex++;
  });

  // Extract text content with image placeholders
  const textContent = doc.body.textContent || '';
  result.text = textContent.trim();

  // Second pass: process images
  const imagePromises = [];
  let currentImageIndex = 0;

  images.forEach((img) => {
    const src = img.src;
    const imageId = imageMap.get(img);

    // Handle base64 encoded images
    if (src && src.startsWith('data:image')) {
      imagePromises.push(
        fetch(src)
          .then((res) => res.blob())
          .then((blob) => {
            // Extract file extension from MIME type
            const mimeType = blob.type;
            const extension = mimeType.split('/')[1] || 'png';
            const fileName = `pasted-image-${currentImageIndex + 1}.${extension}`;

            const file = new File([blob], fileName, { type: mimeType });
            return {
              file,
              dataURL: src,
              type: mimeType,
              id: imageId,
              tag: `[Image@${currentImageIndex}]`,
              index: currentImageIndex,
            };
          })
          .catch((err) => {
            console.error('Failed to process base64 image:', err);
            return null;
          }),
      );
    }
    // Handle file:// URLs (local images from Word)
    else if (src && src.startsWith('file://')) {
      console.warn('Local file URLs cannot be accessed from browser:', src);
    }
    // Handle http/https URLs
    else if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
      imagePromises.push(
        fetch(src)
          .then((res) => res.blob())
          .then((blob) => {
            // Extract file extension from MIME type
            const mimeType = blob.type;
            const extension = mimeType.split('/')[1] || 'png';
            const fileName = `pasted-image-${currentImageIndex + 1}.${extension}`;

            const file = new File([blob], fileName, { type: mimeType });
            return {
              file,
              url: src,
              type: mimeType,
              id: imageId,
              tag: `[Image@${currentImageIndex}]`,
              index: currentImageIndex,
            };
          })
          .catch((err) => {
            console.error('Failed to fetch remote image:', err);
            return null;
          }),
      );
    }

    currentImageIndex++;
  });

  // Wait for all image processing to complete
  const processedImages = await Promise.all(imagePromises);
  result.images = processedImages.filter((img) => img !== null);

  return result;
}

/**
 * Check if clipboard data contains Word HTML content
 * @param {DataTransfer} clipboardData - Clipboard data from paste event
 * @returns {boolean} True if contains Word HTML
 */
export function isWordHTML(clipboardData) {
  if (!clipboardData) return false;

  const html = clipboardData.getData('text/html');
  if (!html) return false;

  // Check for Word-specific markers
  return (
    html.includes('urn:schemas-microsoft-com:office:word') ||
    html.includes('urn:schemas-microsoft-com:office:office') ||
    html.includes('Microsoft Word') ||
    html.includes('mso-')
  );
}
